<template>
    <div>
        <!-- Containing `div` required for page transition -->
        <main v-if="pageData">
            <!--
                PRE CONTENT
            -->
            <div
                v-if="pageData.pre_content?.length"
                style="margin-top: calc(-1 * var(--header-height, 0px))"
            >
                <template v-for="(module, index) in pageData.pre_content">
                    <template v-if="module.__component === 'pre-content.hero'">
                        <ModuleHero
                            :key="module.id + '_' + index"
                            :module-data="module"
                        />
                    </template>
                    <template v-if="module.__component === 'contact.hero'">
                        <ModuleContactHero
                            :key="module.id + '_' + index"
                            :module-data="module"
                        />
                    </template>
                    <template v-if="module.__component === 'pre-content.homepage-hero'">
                        <ModuleHomepageHero
                            :key="module.id + '_' + index"
                            :module-data="module"
                        />
                    </template>
                </template>
            </div>
            <div
                v-else-if="pageData.title"
                class="container text-text-dark bg-background-one py-12 large:py-24"
            >
                <div class="wrap">
                    <h1 class="heading-l">
                        {{ pageData.title }}
                    </h1>
                </div>
            </div>
            <!--
                CONTENT
            -->
            <div
                v-if="pageData.modules?.length"
                class="relative text-text-dark bg-background-one"
            >
                <Modules :modules-data="pageData.modules" />
            </div>
        </main>
    </div>
</template>

<script setup>
    // Manifest
    import { pagePopulate } from '@/manifests/page';

    // Page data
    const route = useRoute();
    const pageSlug = route.params.slug;

    // Preview
    const { isPreview, strapiStatus, previewHeaders } = useStrapiPreview();

    const { locale } = useI18n();

    const { data: pageData } = await useAsyncData(isPreview ? `${pageSlug}-draft-${locale.value}` : `${pageSlug}-${locale.value}`,
        () => $fetch('/pages', {
            baseURL: useRuntimeConfig().public.adminApiUrl,
            headers: previewHeaders,
            query: {
                locale: locale.value,
                status: strapiStatus,
                'filters[slug][$eq]': pageSlug,
                populate: [
                    ...pagePopulate
                ]
            }
        }), {
            transform: response => response.data[0]
        }
    );

    useHead(computed(() => ({
        bodyAttrs: { class: pageData.value?.pre_content?.length ? 'is-light' : '' }
    })));

    // Error handler
    if (!pageData.value && !isPreview) {
        throw createError({
            statusCode: 404,
            message: 'Page not found',
            fatal: true
        });
    }

    // SEO meta
    if (pageData.value) {
        useSeoMetaWithDefault(pageData.value.meta, pageData.value.title);
    }

    // Page specific schema
    if (pageData.value && !pageData.value.meta?.prevent_indexing) {
        useWebPageSchema({
            title: pageData.value.title,
            description: pageData.value.meta?.description,
            image: useMediaUrl()(pageData.value.meta?.og_share_image?.url),
            subtype: pageData.value.meta?.webpage_subtype,
            breadcrumbsId: `${useRuntimeConfig().public.origin}/${pageData.value.slug}#breadcrumb`
        });

        useBreadcrumbSchema();

        const faqItems = computed(() => {
            return pageData.value.modules
                .filter((m) =>
                    m.__component === 'content.accordion' &&
                    m.is_faq === true
                )
                .flatMap(m => m.accordion || [])
                .map(faq => ({
                    question: faq.title,
                    answer: faq.text
                })) || [];
        });

        if (faqItems?.value) {
            useFaqSchema(faqItems.value);
        }
    }
</script>
